import { useCallback } from 'react';

import type { TrackingProperties } from '@change-corgi/tracking-properties';

import type { WaypointCallbackArgs } from '#src/app/shared/components/layout/lazy';
import { Waypoint } from '#src/app/shared/components/scroll';
import { useTrackOnce } from '#src/app/shared/hooks/tracking';

import { PETITION_DETAILS_VIEW_CONTEXT, PETITION_UPDATE_VIEW_EVENT } from './updateTracking';

/**
 * Renders an invisible waypoint that fires `petition_update_view` once as a real update card
 * scrolls into view on the classic petition details page — the control surface for the SPH
 * experiment. Renders nothing (no impression) when `kind` is null — i.e. for synthetic / excluded
 * updates — so a zero-height sentinel never sits next to a card that must not be counted, keeping
 * the funnel comparable with the SPH treatment.
 *
 * `view_context` marks the petition-details surface (the SPH feed sends `signed_petition_home`). The
 * shared event's schema types `petition_id`/`update_id` as numeric, so we send the bare ids: the
 * explicit values here override the page-level `petition_<id>` TrackingContext. `context` is
 * intentionally omitted (the event's schema has no such field), matching the SPH impression.
 */
export const UpdateImpression: React.FC<Props> = ({ kind, petitionId, updateId }) => {
	const trackOnce = useTrackOnce();

	const onPositionChange = useCallback(
		({ currentPosition }: WaypointCallbackArgs) => {
			if (currentPosition === 'below' || kind === null) return;

			const data: TrackingProperties = {
				view_context: PETITION_DETAILS_VIEW_CONTEXT,
				petition_id: petitionId,
				kind,
				update_id: updateId,
			};
			void trackOnce(PETITION_UPDATE_VIEW_EVENT, data);
		},
		[trackOnce, kind, petitionId, updateId],
	);

	if (kind === null) return null;

	return <Waypoint onPositionChange={onPositionChange} />;
};

type Props = Readonly<{
	kind: string | null;
	petitionId: string;
	updateId: string;
}>;
